/**
 * Charger identifier helpers.
 *
 * Two distinct concepts live here:
 *
 *   1. `isChargerCode` — CANONICAL, strict. Matches the current
 *      on-sticker format (`GCEV` + 7 digits) exactly. Used where we
 *      only ever want to accept a *canonical* code — the in-app native
 *      QR scanner sanitiser and the deep-link resolver — because
 *      those surfaces mint new navigations from arbitrary payloads
 *      and must not forward garbage.
 *
 *   2. `isPublicChargerIdentifier` — PERMISSIVE. Matches either a
 *      canonical GCEV code OR a legacy internal station identifier
 *      (base62 short tokens like `KTurSSgk`, manufacturer serials
 *      like `AU105ACG00142P`, dev IDs like `charger001`, etc.). Used
 *      by the `/c/:identifier` entry component and the EvProfile
 *      QR-scan redirect gate — surfaces that must forward EVERY
 *      QR-embedded identifier to the public-charger resolver so
 *      publication rules are enforced regardless of sticker
 *      generation date.
 *
 *      Legacy stations printed QR codes containing their raw
 *      `stations.station_id` value years before the GCEV format
 *      existed. Those stickers are physically in the wild; the
 *      backend `/api/public/chargers/:identifier` route now accepts
 *      both formats and resolves the legacy one to its `stations.public_id`
 *      GCEV internally before applying any business rule, so the
 *      customer sees the same activated / retired / not_yet_active /
 *      network_removed screens either way.
 *
 * Keep the consumer list in sync — a broader or narrower shape
 * anywhere is a bug. If either format ever changes, this file is
 * the one place to edit:
 *
 *   - src/platform/deepLinks.js       (universal / custom-scheme deep links — uses `isChargerCode`)
 *   - src/platform/scanner.js         (native barcode / QR scanner sanitiser — uses `isChargerCode`)
 *   - src/components/ChargerEntry/    (/c/:identifier entry component   — uses `isPublicChargerIdentifier`)
 *   - src/components/Steps/EvProfile/ (legacy QR-scan redirect gate     — uses `isPublicChargerIdentifier`)
 */

export const CHARGER_CODE_RE = /^GCEV\d{7}$/;

/**
 * Permissive charger-identifier shape. Matches GCEV codes AND legacy
 * internal station identifiers (letters, digits, underscore, dot,
 * hyphen; length 3-64). Deliberately excludes URL-path metacharacters
 * (`/`, `?`, `#`, `&`, `%`, whitespace) so we never forward a
 * path-injecting payload to the API.
 *
 * The API is the authority on whether the value resolves to a real
 * charger — this regex just lets safely-typed identifiers through.
 */
export const PUBLIC_CHARGER_IDENTIFIER_RE = /^[A-Za-z0-9._-]{3,64}$/;

/**
 * True when `value` is a well-formed canonical GCEV charger code.
 *
 * Non-string inputs (null, undefined, numbers) return false — the
 * regex would otherwise coerce them and produce misleading matches.
 *
 * @param {unknown} value
 * @returns {boolean}
 */
export function isChargerCode(value) {
  return typeof value === 'string' && CHARGER_CODE_RE.test(value);
}

/**
 * True when `value` is a plausibly-valid public charger identifier —
 * either a canonical GCEV code OR a legacy internal station
 * identifier. Every GCEV code is also a valid public identifier by
 * construction.
 *
 * Callers should hand the value to the API and let it decide whether
 * the identifier resolves; this guard exists to reject empty and
 * path-injecting payloads before we make a request.
 *
 * @param {unknown} value
 * @returns {boolean}
 */
export function isPublicChargerIdentifier(value) {
  return typeof value === 'string' && PUBLIC_CHARGER_IDENTIFIER_RE.test(value);
}
